/**
 * Video with Text Overlay section
 * Play modal supporting a Shopify-hosted <video> OR a YouTube/Vimeo embed,
 * plus a Play-button duration auto-filled from the actual video:
 *   - hosted  -> the <video> element's own duration (loadedmetadata)
 *   - vimeo   -> oEmbed JSON (no SDK / key)
 *   - youtube -> IFrame API (hidden player, getDuration)
 * The Liquid renders a manual duration as the fallback until JS fills it in.
 */
if (!customElements.get('video-with-text-overlay')) {
  class VideoWithTextOverlay extends HTMLElement {
    connectedCallback() {
      this.playBtns = this.querySelectorAll('.vwto__play-cluster .vwto__play-btn');
      this.closeBtn = this.querySelector('.vwto__close-btn');
      this.embedContainer = this.querySelector('.vwto__embed');
      this.iframe = this.querySelector('.vwto__embed-iframe');
      this.modalVideo = this.querySelector('.vwto__embed-video');
      this.durationEl = this.querySelector('[data-vwto-duration]');
      /* Was `video.vwto__bg-media[autoplay]`. The PIM film background carries no
         autoplay attribute — it is hydrated and played by _initLazyMedia below —
         so the attribute filter would have stopped matching it. Shopify-hosted
         backgrounds still render their own autoplay and match either way.

         A PIM film can ship as a desktop/mobile pair (pim.assets.video [0]/[1]),
         in which case both cuts are in the DOM and CSS shows one. bgVideo is then
         whichever matches the current breakpoint — resolved below, once
         mobileQuery exists, and re-resolved on resize. */
      this.bgVideoDesktop = this.querySelector('video.vwto__bg-media--desktop');
      this.bgVideoMobile = this.querySelector('video.vwto__bg-media--mobile');

      this.videoId = this.dataset.videoId;
      this.videoType = this.dataset.videoType;
      this.videoIdMobile = this.dataset.videoIdMobile;
      this.videoTypeMobile = this.dataset.videoTypeMobile;
      // Raw URL (e.g. from an athlete metaobject) parsed into id + type.
      const parsed = this._parseUrl(this.dataset.videoSrc);
      if (parsed && !this.videoId) {
        this.videoId = parsed.id;
        this.videoType = parsed.kind;
      }
      this.mobileQuery = window.matchMedia('(max-width: 1024px)');
      this.bgVideo = this._resolveBgVideo();

      /* Only a real pair needs the resize listener; a single cut serves both. */
      if (this.bgVideoDesktop && this.bgVideoMobile) {
        this._onBgBreakpoint = () => this._swapBgCut();
        this.mobileQuery.addEventListener('change', this._onBgBreakpoint);
      }

      /* Move the player+shim to <body> so it escapes the section's stacking
         context. It is position:fixed at z-index 999 — above the header's 900 —
         but a z-index only competes inside its own stacking context, and
         sections sit inside one, so left in place the nav paints over the
         overlay. Same fix pdp-gallery-apparel-overlay.js uses for its backdrop.
         Safe here because every .vwto__embed rule is scoped to the element
         itself (.vwto__embed.is-open …), not to a descendant of this host. */
      if (this.embedContainer) document.body.appendChild(this.embedContainer);

      this.playBtns.forEach((btn) => btn.addEventListener('click', () => this.play()));
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.stop());
      if (this.embedContainer) {
        this.embedContainer.addEventListener('click', (e) => {
          if (e.target === this.embedContainer) this.stop();
        });
      }

      this._onKeyDown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-playing')) this.stop();
      };
      document.addEventListener('keydown', this._onKeyDown);

      this._initLazyMedia();
    }

    /* ---------------- Lazy media (PIM film only) ----------------
       The PIM background renders with no src and preload="none", and the modal
       video with preload="none" + data-lazy-preload. Both are hydrated together
       when the section nears the viewport, so a section that sits last on the PDP
       costs one poster image until the shopper scrolls near it.

       Paths with nothing lazy (Shopify-hosted background, YouTube/Vimeo) fall
       straight through to _initDuration, unchanged. */
    /* The background cut for the current viewport. With no --desktop/--mobile
       pair this is just the single background, whatever it is (PIM film or a
       Shopify-hosted one), so every non-paired path behaves exactly as before. */
    _resolveBgVideo() {
      if (!this.bgVideoDesktop && !this.bgVideoMobile) {
        return this.querySelector('video.vwto__bg-media');
      }
      return this.mobileQuery.matches
        ? this.bgVideoMobile || this.bgVideoDesktop
        : this.bgVideoDesktop || this.bgVideoMobile;
    }

    /* Assign the real src to the ACTIVE cut only — this is what keeps a pair from
       downloading two masters. Called at hydration and again after a breakpoint
       change, so the newly visible cut fetches then and the hidden one never
       does. Guarded on `.src` so it is safe to call repeatedly. */
    _hydrateBg() {
      if (!this._bgAllowed || !this.bgVideo) return;
      const src = this.bgVideo.dataset.lazySrc;
      if (!src || this.bgVideo.src) return;
      // Set muted from JS as well as markup: assigning src after parse has let
      // Safari evaluate the autoplay policy against a stale muted state.
      this.bgVideo.muted = true;
      this.bgVideo.src = src;
    }

    /* Breakpoint crossed: hand the background over to the other cut. Pauses the
       outgoing one so an off-screen video isn't left decoding, then hydrates and
       plays the incoming one — but only if the section already hydrated, since
       before that the observer hasn't fired and nothing should fetch yet. */
    _swapBgCut() {
      const next = this._resolveBgVideo();
      if (!next || next === this.bgVideo) return;

      if (this.bgVideo && !this.bgVideo.paused) this.bgVideo.pause();
      this.bgVideo = next;

      if (!this._hydrated) return;
      this._hydrateBg();
      this._playBg();
    }

    _initLazyMedia() {
      /* Capability detection only — "is the background lazy at all" and "is
         autoplay allowed". Both cuts of a pair are rendered from the same snippet
         with the same flags, so either one answers for both; the ACTUAL element
         that gets a src is always re-read from this.bgVideo, in _hydrateBg. */
      const bg = this.bgVideo;
      const lazyBgSrc = bg && bg.dataset.lazySrc;
      const lazyModal = this.modalVideo && this.modalVideo.dataset.lazyPreload;

      if (!lazyBgSrc && !lazyModal) {
        this._initDuration();
        return;
      }

      /* Three ways the background stays a still: the section's "Autoplay
         background video" setting is off, or the visitor asked for reduced
         motion, or Data Saver is on. In all three the src is never assigned, so
         the master is never fetched — the poster frame is the whole background
         and the Play button still opens the film. */
      const autoplay = bg && bg.dataset.lazyAutoplay === 'true';
      const reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection && navigator.connection.saveData;
      this._bgAllowed = !!lazyBgSrc && autoplay && !reduceMotion && !saveData;

      const hydrate = () => {
        if (this._hydrated) return;
        this._hydrated = true;

        if (lazyModal) this.modalVideo.preload = this.modalVideo.dataset.lazyPreload;
        // Duration reads the modal video's metadata, so it can only run now.
        this._initDuration();

        // Reads this.bgVideo rather than the cut captured at init: a resize
        // before the section scrolled into view must not fetch the wrong master.
        this._hydrateBg();
        this._playBg();
      };

      if (!('IntersectionObserver' in window)) {
        hydrate();
        return;
      }

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              hydrate();
              this._playBg();
            } else if (this._hydrated && this.bgVideo && !this.bgVideo.paused) {
              // Off screen: stop decoding. Nothing is visible to interrupt.
              this.bgVideo.pause();
            }
          });
        },
        // Start fetching just before it scrolls into view, not as it lands.
        { rootMargin: '300px 0px' }
      );
      this._observer.observe(this);
    }

    /* Resume the background loop — never while the fullscreen player is open,
       and never for a background that was deliberately not hydrated. */
    _playBg() {
      if (!this._bgAllowed || !this._hydrated || !this.bgVideo) return;
      if (this.classList.contains('is-playing')) return;
      // The active cut may have changed while the fullscreen player was open, so
      // make sure this one actually has its src before asking it to play.
      this._hydrateBg();
      if (!this.bgVideo.paused) return;
      const p = this.bgVideo.play();
      if (p && p.catch) p.catch(() => {});
    }

    /* Parse a YouTube/Vimeo URL string into { kind, id }. */
    _parseUrl(url) {
      if (!url) return null;
      let m = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/
      );
      if (m) return { kind: 'youtube', id: m[1] };
      m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (m) return { kind: 'vimeo', id: m[1] };
      return null;
    }

    /* Which source is the main play video for the current viewport.
       A hosted <video> takes precedence; otherwise the YouTube/Vimeo URL. */
    _source() {
      if (this.modalVideo) return { kind: 'hosted' };
      const isMobile = this.mobileQuery && this.mobileQuery.matches;
      const id = isMobile ? this.videoIdMobile || this.videoId : this.videoId || this.videoIdMobile;
      const type = isMobile ? this.videoTypeMobile || this.videoType : this.videoType || this.videoTypeMobile;
      if (id && type) return { kind: type, id: id };
      return null;
    }

    play() {
      const src = this._source();
      if (!src || !this.embedContainer) return;

      if (src.kind === 'hosted') {
        if (!this.modalVideo) return;
        try { this.modalVideo.currentTime = 0; } catch (e) {}
        const p = this.modalVideo.play();
        if (p && p.catch) p.catch(() => {});
      } else if (this.iframe) {
        // enablejsapi/origin: lets assets/datalayer-tracking.js attach a
        // YT.Player for video_played / video_completed dataLayer events.
        this.iframe.src =
          src.kind === 'youtube'
            ? `https://www.youtube.com/embed/${src.id}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
            : `https://player.vimeo.com/video/${src.id}?autoplay=1`;
      } else {
        return;
      }

      this.embedContainer.classList.add('is-open');
      this.embedContainer.setAttribute('aria-hidden', 'false');
      this.classList.add('is-playing');
      window.ScrollLock ? ScrollLock.lock('video-with-text') : (document.body.style.overflow = 'hidden');
      if (this.bgVideo && this.bgVideo.pause) this.bgVideo.pause();
      if (this.closeBtn) this.closeBtn.focus();
    }

    stop() {
      if (!this.embedContainer) return;
      this.embedContainer.classList.remove('is-open');
      this.embedContainer.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-playing');
      window.ScrollLock ? ScrollLock.unlock('video-with-text') : (document.body.style.overflow = '');

      if (this.modalVideo) {
        try {
          this.modalVideo.pause();
          this.modalVideo.currentTime = 0;
          // Reset the element so it's no longer the active media — otherwise a
          // hardware media key resumes the paused video in the background.
          this.modalVideo.load();
        } catch (e) {}
      }

      this.embedContainer.addEventListener(
        'transitionend',
        () => {
          if (!this.embedContainer.classList.contains('is-open') && this.iframe) {
            this.iframe.src = '';
          }
        },
        { once: true }
      );

      if (this.bgVideo && this.bgVideo.play) {
        /* Only ever RESUME a background that was playing to begin with.
           - Lazy (PIM): _playBg re-checks hydrated + allowed, so closing the
             player cannot start the download reduced-motion / Data Saver
             declined.
           - Hosted: `autoplay` is present only when the section's "Autoplay
             background video" setting is on, so the attribute is the record of
             whether it was ever meant to play. Resuming unconditionally here
             turned one open/close of the fullscreen film into a permanently
             looping background on a section configured NOT to autoplay — the
             `[autoplay]` filter this element's selector used to carry was what
             made that unreachable before. */
        if (this.bgVideo.dataset.lazySrc) this._playBg();
        else if (this.bgVideo.hasAttribute('autoplay')) this.bgVideo.play();
      }
      const firstPlayBtn = this.playBtns && this.playBtns[0];
      if (firstPlayBtn) firstPlayBtn.focus();
    }

    /* ---------------- Duration ---------------- */

    _setDuration(seconds) {
      if (!this.durationEl || !seconds || !isFinite(seconds)) return;
      const total = Math.round(seconds);
      const m = Math.floor(total / 60);
      const s = total % 60;
      this.durationEl.textContent = `${m}:${String(s).padStart(2, '0')}`;
    }

    _initDuration() {
      if (!this.durationEl) return;
      const src = this._source();
      if (!src) return;

      if (src.kind === 'hosted' && this.modalVideo) {
        const v = this.modalVideo;
        if (v.readyState >= 1 && v.duration) {
          this._setDuration(v.duration);
        } else {
          v.addEventListener('loadedmetadata', () => this._setDuration(v.duration), { once: true });
        }
      } else if (src.kind === 'vimeo') {
        fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${src.id}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => { if (d && d.duration) this._setDuration(d.duration); })
          .catch(() => {});
      } else if (src.kind === 'youtube') {
        this._youtubeDuration(src.id);
      }
    }

    _youtubeDuration(id) {
      const create = () => {
        const holder = document.createElement('div');
        holder.setAttribute('aria-hidden', 'true');
        holder.style.cssText =
          'position:absolute;width:1px;height:1px;left:-9999px;top:-9999px;overflow:hidden;';
        document.body.appendChild(holder);
        let done = false;
        const finish = (player) => {
          if (done) return;
          const d = player.getDuration && player.getDuration();
          if (d) {
            done = true;
            this._setDuration(d);
            try { player.destroy(); } catch (e) {}
            holder.remove();
          }
        };
        const player = new window.YT.Player(holder, {
          videoId: id,
          events: {
            onReady: () => finish(player),
            onStateChange: () => finish(player),
          },
        });
      };

      if (window.YT && window.YT.Player) {
        create();
        return;
      }
      // Chain onto any existing API-ready callback so multiple sections coexist.
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === 'function') prev();
        create();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const s = document.createElement('script');
        s.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(s);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      // A variant change replaces this whole element (variant-sections-refresh
      // mode 2), so the observer has to go with it or it leaks one per switch.
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      // Same reasoning for the desktop/mobile cut listener.
      if (this._onBgBreakpoint && this.mobileQuery) {
        this.mobileQuery.removeEventListener('change', this._onBgBreakpoint);
        this._onBgBreakpoint = null;
      }
      this.stop();
      // The embed now lives on <body>, so it must be removed explicitly —
      // otherwise a theme-editor re-render leaves an orphan behind each reload.
      this.embedContainer?.remove();
    }
  }

  customElements.define('video-with-text-overlay', VideoWithTextOverlay);
}
